/**
 * The page's own labels: the words vB6 prints that the database has no column
 * for.
 *
 * Every string here is vB6's, carried over unchanged. Nothing in this file is
 * a catalogue field, an allergen claim, a price or an operational rule: the
 * dishes, their allergens, their prices, their production units and every
 * figure in §12 come from `public.menu_context()`. What is here is the
 * fourteen allergen names of Regulation (EU) 1169/2011, the display name of a
 * catalogue category, the line drawing on a category tile, the occasion hint
 * sentences, the three serving windows, the three guest profiles and the
 * three balance choices.
 *
 * Two of them are open and marked so:
 *
 *  - the **leading dishes** of §5.3's pre-selection order are dish IDs, and
 *    they are Balázs's to set. vB6 carries them for two occasions and the
 *    catalogue has no column for them; they are passed into the engine as
 *    `Occasion.leadingDishes` and logged under *Defaults chosen in run3*;
 *  - a category with no entry in `CATEGORY_LABELS` falls back to the
 *    catalogue's own Hungarian word rather than to an invented one, so a new
 *    category reads as itself instead of reading as nothing.
 */

import type { Bilingual } from './lang.ts'

/** Regulation (EU) 1169/2011 Annex II, the fourteen, as vB6 names them. */
export const ALLERGEN_NAMES: Readonly<Record<number, Bilingual>> = {
  1: { hu: 'glutént tartalmazó gabonafélék', en: 'cereals containing gluten' },
  2: { hu: 'rákfélék', en: 'crustaceans' },
  3: { hu: 'tojás', en: 'eggs' },
  4: { hu: 'halak', en: 'fish' },
  5: { hu: 'földimogyoró', en: 'peanuts' },
  6: { hu: 'szójabab', en: 'soybeans' },
  7: { hu: 'tej', en: 'milk' },
  8: { hu: 'diófélék', en: 'tree nuts' },
  9: { hu: 'zeller', en: 'celery' },
  10: { hu: 'mustár', en: 'mustard' },
  11: { hu: 'szezámmag', en: 'sesame seeds' },
  12: { hu: 'kén-dioxid és szulfitok', en: 'sulphur dioxide and sulphites' },
  13: { hu: 'csillagfürt', en: 'lupin' },
  14: { hu: 'puhatestűek', en: 'molluscs' },
}

/**
 * The display name of a catalogue category, keyed by the catalogue's own
 * Hungarian word — which is what `public.dishes.category` holds and what
 * `menu_group_map` in the register is keyed by too. vB6 printed these under
 * each dish name in the menu row.
 */
export const CATEGORY_LABELS: Readonly<Record<string, Bilingual>> = {
  /* Run 19 §13: the catalogue says *Wrap pinwheel* since 16 October (Barna),
     and this is the page's own label for the category, so it says the same.
     The dish names themselves are catalogue data and reach the project
     through `pnpm catalogue:sync`, never from here. */
  'wrap karika': { hu: 'Wrap karika', en: 'Wrap pinwheel' },
  'nagy szendvics': { hu: 'Nagy szendvics', en: 'Large sandwich' },
  'háromszög szendvics': { hu: 'Háromszög szendvics', en: 'Triangle sandwich' },
  kanapé: { hu: 'Kanapé', en: 'Canapé' },
  'édes falatka': { hu: 'Édes falatka', en: 'Sweet bite' },
  'pohárkás desszert': { hu: 'Pohárkás desszert', en: 'Dessert in a glass' },
  'séf ajánlata': { hu: 'Séf ajánlata', en: "Chef's selection" },
  'sós snack': { hu: 'Sós snack', en: 'Savoury snack' },
  zöldség: { hu: 'Zöldség', en: 'Vegetables' },
  pékáru: { hu: 'Pékáru', en: 'Bakery' },
  ital: { hu: 'Ital', en: 'Drinks' },
  gyümölcs: { hu: 'Gyümölcs', en: 'Fruit' },
}

/**
 * The four §5.11 price categories, by the key the engine returns on an
 * `OrderLine`. One copy: 04 Összesítés heads its tables with these and the
 * offer card of `build/FIXES.md` item 76 groups its dishes by them, and a
 * second copy is a second thing to keep in step (§9 of
 * `docs/overnight_constraints.md`).
 */
export const PRICE_CATEGORY_LABELS: Readonly<Record<string, Bilingual>> = {
  sos: { hu: 'Sós', en: 'Savoury' },
  edes: { hu: 'Édes', en: 'Sweet' },
  gyumolcs: { hu: 'Gyümölcs', en: 'Fruit' },
  ital: { hu: 'Italok', en: 'Drinks' },
}

/**
 * The category tile of §5.8: what a dish row shows when photographs are on
 * and the dish has no photograph of its own. The paths are vB6's, drawn in a
 * 40×40 box; `CHEF_TILE` is the fallback for a category with no drawing, as
 * it was in vB6.
 */
export const CATEGORY_TILES: Readonly<Record<string, string>> = {
  'wrap karika':
    '<circle cx="20" cy="20" r="13"/><path d="M20 20m-7 0a7 7 0 1 0 14 0a7 7 0 1 0-14 0"/><circle cx="20" cy="20" r="2"/>',
  'nagy szendvics':
    '<rect x="7" y="13" width="26" height="6" rx="3"/><path d="M8 23h24"/><rect x="7" y="26" width="26" height="4" rx="2"/>',
  'háromszög szendvics': '<path d="M8 30L20 9l12 21z"/><path d="M12 25h16"/>',
  kanapé: '<rect x="8" y="22" width="24" height="7" rx="2"/><circle cx="20" cy="17" r="4"/>',
  'séf ajánlata': '<circle cx="20" cy="22" r="8"/><path d="M14 14c0-4 12-4 12 0"/>',
  'sós snack': '<circle cx="20" cy="20" r="9"/><path d="M15 18h2M22 23h2M20 15h1"/>',
  pékáru: '<path d="M8 25c0-8 24-8 24 0v3H8z"/><path d="M14 20l2 4M20 19v5M26 20l-2 4"/>',
  'édes falatka': '<rect x="9" y="12" width="22" height="17" rx="3"/><path d="M9 19h22"/>',
  'pohárkás desszert': '<path d="M12 10h16l-2 20H14z"/><path d="M13 17h14"/>',
  zöldség: '<path d="M12 30l16-20M16 30l14-14M10 26l12-16"/>',
  ital: '<path d="M17 8h6v5l3 4v14H14V17l3-4z"/>',
  gyümölcs:
    '<circle cx="16" cy="23" r="8"/><circle cx="25" cy="21" r="6"/><path d="M20 13c1-3 4-5 7-5"/>',
}

export const CHEF_TILE = CATEGORY_TILES['séf ajánlata'] as string

/**
 * §6.1's occasions as the page introduces them: one sentence under the
 * chosen button. The figures behind each occasion — the two points and the
 * §6.6 dish range — come from `public.occasions` and are not restated here.
 */
export const OCCASION_HINTS: Readonly<Record<string, Bilingual>> = {
  coffee: { hu: '30–60 perc. Édes hangsúlyú.', en: '30–60 minutes. Leans sweet.' },
  munka: { hu: '1–2 óra, az ebédet kiváltja.', en: '1–2 hours, replaces lunch.' },
  /* **R18-2 reversed (DECIDED 20261019, Barna).** The two reception hints
     are gone; `DECISIONS_LOG.md` records the words they used to carry. The
     daytime one stated a length of its own, which contradicts the duration
     the page takes from the start and end times, and its Hungarian was
     ungrammatical; the evening one said what the meal question decides.
     Under *Állófogadás* the page keeps `#fogadasBuffetNote` and, under
     *Igen* and *Nem*, `mealNote` — between them they say everything the
     hints said. Pinned in `brand.test.tsx`. */
  /* `build/FIXES.md` item 73 (F2; Barna's ruling 2 of 8 October 2026): the
     two conference occasions became one, and the hint is the brief's own
     words, which are final. It is no longer a description of the day — the
     three controls of `081` ask which parts of it we cater. */
  konf: {
    hu: 'Válassza ki, mely alkalmakra kér tőlünk ételt.',
    en: 'Choose which parts of the day you want us to cater.',
  },
}

/**
 * §5.3 pre-selection order, step 2: the dishes that lead a side for an
 * occasion. **OPEN — Balázs's to set.** The catalogue has no column for it
 * and `public.occasions` has no field for it; vB6 carries these two lists and
 * this port carries the same two, so the menu opens on the same dishes it
 * opened on in the prototype. Logged in `DECISIONS_LOG.md` under *Defaults
 * chosen in run3, for Barna*. A dish ID that does not publish is simply never
 * found, so an out-of-date entry costs a rank, not a crash.
 */
export const OCCASION_LEADING_DISHES: Readonly<
  Record<string, { sav?: readonly string[]; sw?: readonly string[] }>
> = {
  coffee: {
    sav: ['SO01', 'KA02', 'KA03', 'KA06', 'KA09'],
    sw: ['TE01', 'TE02', 'SO04'],
  },
  /* F2: *Lead dishes: those of Kávészünet.* `konf_eb` carried this list and
     `konf` carries the same one. */
  konf: {
    sav: ['SO01', 'KA02', 'KA03', 'KA06', 'KA09'],
    sw: ['TE01', 'TE02', 'SO04'],
  },
}

/**
 * §6.2, the serving window. The keys are `public.serving_window`'s enum
 * values, which is what `duration_mult_*` in the register and
 * `orders.serving_window` both use; vB6 spelled them h2…h5 and the
 * multipliers are the register's either way.
 */
export const SERVING_WINDOWS: readonly { key: string; label: Bilingual }[] = [
  { key: '2h', label: { hu: 'legfeljebb kb. 2 óra', en: 'up to about 2 hours' } },
  { key: '3h', label: { hu: '3 óra', en: '3 hours' } },
  { key: '4h', label: { hu: '4 óra', en: '4 hours' } },
  { key: '5h_plus', label: { hu: '5 óra vagy több', en: '5 hours or more' } },
]

/** §6.3, *Milyen étkű a közönség?*. The keys are `public.guest_profile`'s. */
export const GUEST_PROFILES: readonly {
  key: string
  label: Bilingual
  example: Bilingual | null
}[] = [
  { key: 'light', label: { hu: 'Könnyed', en: 'Light' }, example: { hu: 'pl. networking', en: 'e.g. networking' } },
  { key: 'average', label: { hu: 'Átlagos', en: 'Average' }, example: null },
  { key: 'hearty', label: { hu: 'Kiadós', en: 'Hearty' }, example: { hu: 'pl. sportcsapat', en: 'e.g. a sports team' } },
]

/**
 * The savoury/sweet balance of step 01 (`build/FIXES.md` item 2), in the
 * order it is shown. Three discrete choices, no slider; *Javasolt* is the
 * default. How far the balance moves and the floor it stops at are
 * `balance_shift` and `balance_floor` in the register, both PROPOSED for
 * Balázs to calibrate.
 */
export const BALANCE_CHOICES: readonly { key: 'sav' | 'rec' | 'sw'; label: Bilingual }[] = [
  { key: 'sav', label: { hu: 'Több sós', en: 'More savoury' } },
  { key: 'rec', label: { hu: 'Javasolt', en: 'Recommended' } },
  { key: 'sw', label: { hu: 'Több édes', en: 'More sweet' } },
]

/**
 * §5.8: the photographs in the opening strip. DECIDED 20261101 (run prompt
 * §C.3): three event photos, in this order, the first in the wide column —
 * `catering (3)`, `(8)` and `(13)`, made by `scripts/prepare_static_photos.mjs`
 * (each also at 640 px, `strip640`). `strip-1/2/4.webp` stay in the repo,
 * unreferenced.
 */
export const STRIP_SHOTS: readonly { file: string | null; caption: Bilingual }[] = [
  {
    file: '/photos/strip/strip-c03.webp',
    caption: { hu: 'A dobozok, ahogy megérkeznek', en: 'The boxes as they arrive' },
  },
  {
    file: '/photos/strip/strip-c08.webp',
    caption: { hu: 'Közeli kép egy vegyes kínálatról', en: 'A close-up of a mixed spread' },
  },
  {
    file: '/photos/strip/strip-c13.webp',
    caption: { hu: 'Egy nyitott doboz az asztalon', en: 'An open box on a table' },
  },
]

/** The strip photos' alt texts, in STRIP_SHOTS order (run prompt §C.3). */
export const STRIP_ALTS: readonly Bilingual[] = [
  { hu: 'Ablakos catering doboz a Planteen hűtődobozán', en: 'A windowed catering box on a Planteen cool box' },
  {
    hu: 'Szendvicsek, wrapek és kanapék tálcán, gyümölcslevekkel',
    en: 'Sandwiches, wraps and canapés on a tray, with juices',
  },
  {
    hu: 'Nyitott egyéni doboz wrapekkel és desszerttel egy fa asztalon',
    en: 'An open individual box with wraps and a dessert on a wooden table',
  },
]

/** The 640 px variant of a strip photo. */
export const strip640 = (file: string): string => file.replace(/\.webp$/, '-640.webp')

/** The opening band's photograph (run prompt §C.2, DECIDED 20261101). */
export const HERO_SHOT: Readonly<{ file: string; small: string; caption: Bilingual; alt: Bilingual }> = {
  file: '/photos/hero/spread-1448.webp',
  small: '/photos/hero/spread-800.webp',
  caption: { hu: 'Példa egy vegyes dobozra', en: 'An example of a mixed box' },
  alt: {
    hu: 'Vegyes catering doboz felülről: wrapek, szendvicsek, pohárkás desszertek, energiagolyók',
    en: 'A mixed catering box from above: wraps, sandwiches, desserts in cups, energy balls',
  },
}

/*
 * Dish photos (DECIDED 20261101, photo prompt §5): the list of five dish IDs
 * and their files in `public/photos/` are gone. A dish's photo is the
 * publication's `photo` (`MenuDish.photo`, `photoOf` in model.ts), synced
 * from the Drive folder into Storage; a dish without one shows its drawn
 * category tile, as before.
 */
